import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";
import intraverseBankAccountsApi from "../../../../controllers/settings/intraverseBankAccountsApi";
import { ListShimmer } from "../../../../components/common/reportDashBShimmer/ListShimmer";
import CommonPaginationSection from "../../../../components/common/CommonPaginationSection";
import IntraverseBankAccountForm from "./IntraverseBankAccountForm";

const SEARCH_BY = "bankName,accountName";
const DEFAULT_PER_PAGE = 10;

const NGN_CURRENCY = {
  _id: "ngn-default",
  name: "Nigerian Naira",
  code: "NGN",
  symbol: "₦",
};

function getAccountCurrency(account) {
  return (
    account?.currency || account?.currencyCode || account?.currency?.code || ""
  );
}

export default function IntraverseBankAccountsModal({
  open,
  setOpen,
  currencies = [],
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState("list");
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteAccount, setDeleteAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
  });

  const currencyOptions = useMemo(() => {
    const others = (currencies || []).filter(
      (currency) => currency?.code !== "NGN",
    );
    return [NGN_CURRENCY, ...others];
  }, [currencies]);

  const resetListState = () => {
    setView("list");
    setEditingAccount(null);
    setDeleteAccount(null);
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrencyFilter("");
    setPagination({ page: 1, perPage: DEFAULT_PER_PAGE, total: 0 });
  };

  const handleClose = () => {
    resetListState();
    setOpen?.(false);
  };

  const openCreate = () => {
    setEditingAccount(null);
    setDeleteAccount(null);
    setView("form");
  };

  const openEdit = (account) => {
    setDeleteAccount(null);
    setEditingAccount(account);
    setView("form");
  };

  const backToList = () => {
    setEditingAccount(null);
    setDeleteAccount(null);
    setView("list");
  };

  const loadAccounts = ({
    keyword = debouncedSearch,
    currency = currencyFilter,
    page = pagination.page,
    perPage = pagination.perPage,
  } = {}) => {
    setLoading(true);
    const trimmed = keyword.trim();
    const params = {
      limit: perPage,
      offset: (page - 1) * perPage,
      ...(trimmed ? { searchBy: SEARCH_BY, keyword: trimmed } : {}),
      ...(currency ? { filterBy: "currency", filterValue: currency } : {}),
    };

    return intraverseBankAccountsApi
      .getAccounts(params)
      .then((res) => {
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];
        const count = res?.meta?.total ?? res?.data?.meta?.total ?? list.length;

        setAccounts(list);
        setPagination((prev) => ({
          ...prev,
          page,
          perPage,
          total: count,
        }));
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = async () => {
    if (!deleteAccount?._id) return;

    setDeleting(true);
    const res = await intraverseBankAccountsApi.delete(deleteAccount._id);
    setDeleting(false);

    if (res?.return) {
      enqueueSnackbar("Bank account deleted successfully.", {
        variant: "success",
      });
      setDeleteAccount(null);
      loadAccounts({
        keyword: debouncedSearch,
        currency: currencyFilter,
        page: 1,
      });
    } else {
      enqueueSnackbar(res?.msg || "Unable to delete bank account.", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    resetListState();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!open || view !== "list") return;
    loadAccounts({
      keyword: debouncedSearch,
      currency: currencyFilter,
      page: 1,
      perPage: pagination.perPage,
    });
  }, [open, view, debouncedSearch, currencyFilter]);

  if (!open) return null;

  const isFormView = view === "form";
  const hasSearch = Boolean(debouncedSearch);
  const hasCurrencyFilter = Boolean(currencyFilter);
  const hasFilters = hasSearch || hasCurrencyFilter;
  const isEmpty = !loading && accounts.length === 0;
  const showPagination = !loading && pagination.total > 0;
  const showListControls =
    !isEmpty ||
    hasFilters ||
    pagination.total > 0 ||
    currencyOptions.length > 0;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        aria-label="Close bank accounts drawer"
        className="absolute inset-0 bg-black/40 border-0 cursor-pointer"
        onClick={handleClose}
      />

      <aside
        className="relative z-[1] flex h-full w-full sm:w-[520px] md:w-[640px] max-w-full flex-col bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Intraverse bank accounts"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[#E2E8F0] bg-[#0B2B4A] p-4 text-white">
          <div className="flex items-center gap-2 min-w-0">
            {isFormView ? (
              <button
                type="button"
                onClick={backToList}
                className="shrink-0 rounded p-1 text-white hover:bg-white/10"
                aria-label="Back to accounts list"
              >
                <Icon icon="material-symbols:arrow-back" className="text-2xl" />
              </button>
            ) : null}
            <div className="flex flex-col gap-0.5 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white m-0">
                {isFormView
                  ? editingAccount
                    ? "Update bank account"
                    : "Create bank account"
                  : "Intraverse bank accounts"}
              </h2>
              <span className="text-xs sm:text-sm text-white/80">
                {isFormView
                  ? editingAccount
                    ? "Edit this Intraverse bank account"
                    : "Add a new Intraverse bank account"
                  : loading
                    ? "Loading accounts..."
                    : `${pagination.total} account${pagination.total === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 rounded p-1 text-white hover:bg-white/10"
            aria-label="Close"
          >
            <Icon icon="material-symbols:close" className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isFormView ? (
            <IntraverseBankAccountForm
              currencies={currencies}
              data={editingAccount}
              onCancel={backToList}
              onSuccess={() => {
                backToList();
                loadAccounts({
                  keyword: debouncedSearch,
                  currency: currencyFilter,
                  page: 1,
                });
              }}
            />
          ) : (
            <div className="flex flex-col gap-3 min-h-full">
              {showListControls ? (
                <>
                  <div className="relative">
                    <Icon
                      icon="mdi:magnify"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-[#6D7C94]"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search by bank or account name"
                      className="w-full h-12 pl-10 pr-4 border border-[#DBDBDB] rounded-lg placeholder:text-[#A4B1C1] focus:outline-none focus:border-primary1"
                    />
                  </div>

                  <div className="flex gap-2 overflow-x-auto pb-1 scroll-hide">
                    <button
                      type="button"
                      onClick={() => setCurrencyFilter("")}
                      className={`shrink-0 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${
                        !currencyFilter
                          ? "bg-primary1 text-white border-primary1"
                          : "bg-white text-[#6D7C94] border-[#DBDBDB] hover:border-primary1 hover:text-primary1"
                      }`}
                    >
                      All
                    </button>
                    {currencyOptions.map((currency) => {
                      const selected = currencyFilter === currency.code;
                      return (
                        <button
                          key={currency._id || currency.code}
                          type="button"
                          onClick={() =>
                            setCurrencyFilter(selected ? "" : currency.code)
                          }
                          className={`shrink-0 px-3 py-2 rounded-full border text-sm font-medium transition-colors whitespace-nowrap ${
                            selected
                              ? "bg-primary1 text-white border-primary1"
                              : "bg-white text-[#6D7C94] border-[#DBDBDB] hover:border-primary1 hover:text-primary1"
                          }`}
                        >
                          {currency.symbol ? `${currency.symbol} ` : ""}
                          {currency.code}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={openCreate}
                    className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-[#C5D0DE] bg-transparent px-4 py-3.5 text-sm font-medium text-primary1 hover:border-primary1 hover:bg-[#F0F6FF] transition-colors"
                  >
                    <Icon icon="mdi:plus" className="text-lg" />
                    Add another account
                  </button>
                </>
              ) : null}

              {loading ? (
                <ListShimmer rows={5} />
              ) : isEmpty && !hasFilters ? (
                <div className="flex flex-col items-center justify-center text-center gap-4 py-16 px-4">
                  <div className="w-14 h-14 rounded-full bg-[#F0F6FF] flex items-center justify-center">
                    <Icon
                      icon="mdi:bank-off-outline"
                      className="text-3xl text-primary1"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-base font-semibold text-[#1A1A1A] m-0">
                      No bank accounts yet
                    </h3>
                    <p className="text-sm text-[#6D7C94] m-0 max-w-xs">
                      There are no Intraverse bank accounts set up. Create one
                      to get started.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openCreate}
                    className="h-11 px-5 rounded-lg bg-primary1 text-white text-sm font-semibold hover:opacity-90"
                  >
                    Create bank account
                  </button>
                </div>
              ) : isEmpty && hasFilters ? (
                <div className="flex flex-col items-center justify-center text-center gap-2 py-12 px-4">
                  <Icon
                    icon="mdi:magnify-close"
                    className="text-3xl text-[#6D7C94]"
                  />
                  <h3 className="text-base font-semibold text-[#1A1A1A] m-0">
                    No matching accounts
                  </h3>
                  <p className="text-sm text-[#6D7C94] m-0">
                    Try a different search or currency filter.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {accounts.map((account, index) => {
                    const currency = getAccountCurrency(account);
                    return (
                      <div
                        key={
                          account?._id || `${account?.accountNumber}-${index}`
                        }
                        className="flex flex-col gap-3 rounded-lg border border-[#E0E0E0] bg-[#F9F9F9] px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="font-semibold text-sm text-[#1A1A1A]">
                              {account?.bankName ||
                                account?.bank ||
                                "Bank account"}
                            </span>
                            <span className="text-xs text-[#6D7C94]">
                              {account?.accountName || "—"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {currency ? (
                              <span className="text-xs font-semibold text-primary1 px-2 py-1 rounded-md bg-white border border-[#E0E0E0]">
                                {currency}
                              </span>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openEdit(account)}
                              className="p-2 rounded-md bg-white border border-[#E0E0E0] text-[#6D7C94] hover:text-primary1 hover:border-primary1"
                              aria-label="Edit bank account"
                            >
                              <Icon icon="tabler:edit" className="text-base" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteAccount(account)}
                              className="p-2 rounded-md bg-white border border-[#E0E0E0] text-red-500 hover:border-red-400"
                              aria-label="Delete bank account"
                            >
                              <Icon
                                icon="material-symbols:delete-outline"
                                className="text-base"
                              />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm text-[#1A1A1A]">
                            {account?.accountNumber || "—"}
                          </span>
                          {account?.isDefault ? (
                            <span className="text-xs text-[#6D7C94]">
                              Default
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {showPagination ? (
                <div className="mt-auto">
                  <CommonPaginationSection
                    page={pagination.page}
                    perPage={pagination.perPage}
                    total={pagination.total}
                    callback={(page, perPage) => {
                      loadAccounts({
                        keyword: debouncedSearch,
                        currency: currencyFilter,
                        page,
                        perPage: Number(perPage) || DEFAULT_PER_PAGE,
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </aside>

      {deleteAccount ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close delete confirmation"
            className="absolute inset-0 bg-black/50 border-0 cursor-pointer"
            onClick={() => !deleting && setDeleteAccount(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-bank-account-title"
            className="relative z-[1] w-full max-w-md rounded-xl bg-white shadow-xl p-5 flex flex-col gap-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h3
                  id="delete-bank-account-title"
                  className="text-lg font-semibold text-[#1A1A1A] m-0"
                >
                  Delete bank account?
                </h3>
                <p className="text-sm text-[#6D7C94] m-0">
                  This will permanently remove{" "}
                  <span className="font-medium text-[#1A1A1A]">
                    {deleteAccount.bankName || "this account"}
                  </span>
                  {deleteAccount.accountNumber
                    ? ` (${deleteAccount.accountNumber})`
                    : ""}
                  . This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteAccount(null)}
                className="shrink-0 rounded p-1 text-[#6D7C94] hover:bg-[#F5F7FA]"
                aria-label="Close"
              >
                <Icon icon="material-symbols:close" className="text-xl" />
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteAccount(null)}
                className="h-10 px-4 rounded-lg border border-[#DBDBDB] bg-white text-sm font-medium text-[#6D7C94]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDelete}
                className="h-10 px-4 rounded-lg bg-[#B81F25] text-white text-sm font-semibold disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
